import { useCallback } from 'react'

import { ButtonTypes, LinkTypes } from '@cox/core-ui8//dist/Button'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import ReactProductComparisonTeaserSkeleton from './ReactProductComparisonTeaserSkeleton'
import { offerElementsClasses } from '../../../constants'
import { getAttributeValue, hasCFConditionals } from '../../../utils/rule-builder-util'
import { getAnchorInfo } from '../../../utils/teaser-util'
import ReactBadge from '../../ReactBadge'
import ReactButton from '../../ReactButton'
import ReactList, { ListType } from '../../ReactList'
import ReactPrice, { PriceVariant } from '../../ReactPrice'
import ReactText from '../../ReactText'
import ReactTeaserCard from '../ReactTeaserCard'
import { ComparisonVariation, Elements, ReactProductComparisonTeaserProps } from '../types'
import './ReactProductComparisonTeaser.scss'

const ReactProductComparisonTeaser = (props: ReactProductComparisonTeaserProps): JSX.Element => {
  const {
    contentFragments,
    cardType,
    ruleExpression = '',
    priceTagIcon,
    priceDropdownIcon,
    priceDropdownIconAlt,
    ruleType,
    condition,
    contentType,
    tokenProperties,
    variation,
    maxSlides
  } = props

  const flexOffers = useFlexOffers(ruleExpression, contentType, contentFragments, maxSlides, 'comparisonteaser')

  const renderCard = useCallback(
    (elements: Elements, index: number, id: string) => {
      const {
        contentFragmentId = { value: '' },
        contentBadgeColor = { value: '' },
        contentBadgeText = { value: '' },
        eyebrow = { value: '', title: '' },
        heading = { value: '' },
        headingIcon = { value: '' },
        headingIconAltText = { value: '' },
        headingUnit = { value: '' },
        additionalNote = { value: '' },
        description = { value: '' },
        bulletPoints = { value: '' },
        price = { value: '' },
        pricedropdown = { value: '' },
        // Primary button props:
        buttonTextPrimary = { value: '' },
        buttonLinkPrimary = { value: '' },
        buttonLinkTypePrimary = { value: '' },
        buttonLinkNewTabPrimary = { value: '' },
        linkTypeButtonPrimary = { value: '' },
        linkIdButtonPrimary = { value: '' },
        // Secondary button props:
        buttonTextSecondary = { value: '' },
        buttonLinkSecondary = { value: '' },
        buttonLinkNewTabSecondary = { value: '' },
        buttonLinkTypeSecondary = { value: '' },
        linkTypeButtonSecondary = { value: '' },
        linkIdButtonSecondary = { value: '' },
        // Tertiary button props:
        buttonTextTertiary = { value: '' },
        buttonLinkTertiary = { value: '' },
        buttonLinkNewTabTertiary = { value: '' },
        buttonLinkTypeTertiary = { value: '' },
        linkTypeButtonTertiary = { value: '' },
        linkIdButtonTertiary = { value: '' }
      } = elements
      if (flexOffers?.loading) {
        return <ReactProductComparisonTeaserSkeleton />
      }
      const { hasAnchorLink: primaryButtonHasAnchorLink, anchorId: primaryButtonAnchorId } = getAnchorInfo(
        buttonLinkPrimary?.value
      )

      const { hasAnchorLink: secondaryButtonHasAnchorLink, anchorId: secondaryButtonAnchorId } = getAnchorInfo(
        buttonLinkSecondary?.value
      )

      const { hasAnchorLink: tertiaryButtonHasAnchorLink, anchorId: tertiaryButtonAnchorId } = getAnchorInfo(
        buttonLinkSecondary?.value
      )

      const getOptionsAttributeValue = (attribute: any, propertyName?: string) => {
        const bulletAttributeArray = []

        // Get the value for each bullet if conditions are met:
        const getAttribute = (propertyName: string, baseProperty: string) => {
          const refinedAttribute = { ...attribute }

          function checkBulletNumber(bullet: any) {
            if (propertyName == 'bulletPoints') {
              return bullet?.points == baseProperty
            } else if (propertyName == 'bulletIcon') {
              return bullet?.bulletIcons == baseProperty
            } else if (propertyName == 'bulletIconAltText') {
              return bullet?.iconsAltText == baseProperty
            }
          }

          let refinedBullets = refinedAttribute?.value
          refinedBullets = refinedBullets && JSON.parse(refinedBullets).filter(checkBulletNumber)
          refinedAttribute.value = refinedBullets && JSON.stringify(refinedBullets)
          const attributeValue = getAttributeValue(refinedAttribute, flexOffers, index, tokenProperties, baseProperty)
          return attributeValue
        }

        // For max of 4 bullets:
        for (let i = 1; i <= 4; i++) {
          // Get base property name:
          let baseProperty = ''
          if (propertyName == 'bulletPoints') {
            baseProperty = `bulletText${i}`
          } else if (propertyName == 'bulletIcon') {
            baseProperty = `bulletIcons${i}`
          } else if (propertyName == 'bulletIconAltText') {
            baseProperty = `bulletIconAltText${i}`
          }

          // Get attribute for the bullet:
          const attribute = propertyName && getAttribute(propertyName, baseProperty)

          // If attribute meets conditions and exists, push into the attribute array:
          if (attribute) {
            bulletAttributeArray.push(attribute)
          }
        }

        return bulletAttributeArray
      }

      const getValue = (attribute: any, propertyName?: string) => {
        const hasJsonAttribute = hasCFConditionals(attribute)
        if (hasJsonAttribute) {
          const jsonAttribute = (elements as any)?.[`${propertyName}_json`]
          if (propertyName?.includes('bullet')) {
            return getOptionsAttributeValue(jsonAttribute, propertyName)
          } else return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName, hasJsonAttribute)
        }
        return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
      }

      const contentFragmentIdValue = getValue(contentFragmentId, 'contentFragmentId')
      const contentBadgeTextValue = getValue(contentBadgeText, 'contentBadgeText')
      const contentBadgeColorValue = getValue(contentBadgeColor, 'contentBadgeColor')
      const eyebrowValue = getValue(eyebrow, 'eyebrow')
      const headingIconValue = getValue(headingIcon, 'headingIcon')
      const headingIconAltTextValue = getValue(headingIconAltText, 'headingIconAltText')
      const headingValue = getValue(heading, 'heading')
      const headingUnitValue = getValue(headingUnit, 'headingUnit')
      const additionalNoteValue = getValue(additionalNote, 'additionalNote')
      const descriptionValue = getValue(description, 'description')
      const priceValue = getValue(price, 'price')
      const pricedropdownValue = getValue(pricedropdown, 'pricedropdown')

      // Primary button values:
      const buttonTextPrimaryValue = getValue(buttonTextPrimary, 'buttonTextPrimary')
      const buttonLinkPrimaryValue = getValue(buttonLinkPrimary, 'buttonLinkPrimary')
      const linkTypeButtonPrimaryValue = linkTypeButtonPrimary?.value
      const linkIdButtonPrimaryValue = getValue(linkIdButtonPrimary, 'linkIdButtonPrimary')

      // Secondary button values:
      const buttonTextSecondaryValue = getValue(buttonTextSecondary, 'buttonTextSecondary')
      const buttonLinkSecondaryValue = getValue(buttonLinkSecondary, 'buttonLinkSecondary')
      const linkTypeButtonSecondaryValue = linkTypeButtonSecondary?.value
      const linkIdButtonSecondaryValue = getValue(linkIdButtonSecondary, 'linkIdButtonSecondary')

      // Tertiary button values:
      const buttonTextTertiaryValue = getValue(buttonTextTertiary, 'buttonTextTertiary')
      const buttonLinkTertiaryValue = getValue(buttonLinkTertiary, 'buttonLinkTertiary')
      const linkTypeButtonTertiaryValue = linkTypeButtonTertiary?.value
      const linkIdButtonTertiaryValue = getValue(linkIdButtonTertiary, 'linkIdButtonTertiary')

      const hasHeadingIcon = headingIcon?.value || headingIconAltText?.value

      const primaryButtonType = buttonLinkTypePrimary?.value || ButtonTypes.PRIMARY
      const secondaryButtonType = buttonLinkTypeSecondary?.value || ButtonTypes.SECONDARY
      const tertiaryButtonType = buttonLinkTypeTertiary?.value || ButtonTypes.TERTIARY
      /*
      const bulletList = {
        icon: getImageSrc(getValue(bulletIcon, 'bulletIcon')),
        altIcon: getValue(bulletIconAltText, 'bulletIconAltText'),
        items: bulletPoints?.value?.map((bullet: any, index: number) => {
          if (index < 3) return getValue(bullet)
        })
      }
      */

      const getProductComparisonTeaserButtons = () => {
        /* Primary Button */
        const primaryButtonLink = primaryButtonHasAnchorLink ? buttonLinkPrimary?.value : buttonLinkPrimaryValue
        const displayPrimaryButton = buttonTextPrimaryValue && buttonTextPrimaryValue.length > 0

        /* Secondary Button */
        const secondaryButtonLink = secondaryButtonHasAnchorLink ? buttonLinkSecondary?.value : buttonLinkSecondaryValue
        const displaySecondaryButton = buttonTextSecondaryValue && buttonTextSecondaryValue.length > 0

        /* Tertiary Button */
        const tertiaryButtonLink = tertiaryButtonHasAnchorLink ? buttonLinkTertiary?.value : buttonLinkTertiaryValue
        const displayTertiaryButton = buttonTextTertiaryValue && buttonTextTertiaryValue.length > 0

        const displayThreeButtons =
          displayPrimaryButton && displaySecondaryButton && displayTertiaryButton ? 'display-three-buttons' : ''

        return (
          <div className={`product-comparision-teaser-buttons ${displayThreeButtons}`}>
            {displayPrimaryButton && (
              <div className='pct-button primary-teaser-button'>
                <ReactButton
                  buttonTypes={primaryButtonType as ButtonTypes}
                  text={buttonTextPrimaryValue}
                  anchor={primaryButtonAnchorId}
                  linkUrl={primaryButtonLink}
                  openInNewTab={!!buttonLinkNewTabPrimary?.value}
                  linkType={linkTypeButtonPrimaryValue as LinkTypes}
                  linkId={linkIdButtonPrimaryValue}
                  index={index}
                  ruleExpression={ruleExpression}
                />
              </div>
            )}
            {displaySecondaryButton && (
              <div className='pct-button secondary-teaser-button'>
                <ReactButton
                  buttonTypes={secondaryButtonType as ButtonTypes}
                  text={buttonTextSecondaryValue}
                  anchor={secondaryButtonAnchorId}
                  linkUrl={secondaryButtonLink}
                  openInNewTab={!!buttonLinkNewTabSecondary?.value}
                  linkType={linkTypeButtonSecondaryValue as LinkTypes}
                  linkId={linkIdButtonSecondaryValue}
                  index={index}
                  ruleExpression={ruleExpression}
                />
              </div>
            )}
            {displayTertiaryButton && (
              <div className='pct-button tertiary-teaser-button'>
                <ReactButton
                  buttonTypes={tertiaryButtonType as ButtonTypes}
                  text={buttonTextTertiaryValue}
                  anchor={tertiaryButtonAnchorId}
                  linkUrl={tertiaryButtonLink}
                  openInNewTab={!!buttonLinkNewTabTertiary?.value}
                  linkType={linkTypeButtonTertiaryValue as LinkTypes}
                  linkId={linkIdButtonTertiaryValue}
                  index={index}
                  ruleExpression={ruleExpression}
                />
              </div>
            )}
          </div>
        )
      }

      const getMainPriceDetails = () => {
        if (variation == ComparisonVariation.PRICE_DROPDOWN) {
          return (
            <ReactPrice
              variant={PriceVariant.DEFAULT}
              priceTagIcon={priceTagIcon}
              priceDropdownIcon={priceDropdownIcon}
              priceDropdownIconAlt={priceDropdownIconAlt}
              cardType={cardType}
              variation={variation}
              ruleExpression={ruleExpression}
              index={index}
              elements={elements}
              tokenProperties={tokenProperties}
            />
          )
        } else if (variation == ComparisonVariation.NO_PRICEDROPDOWN) {
          return (
            <ReactPrice
              variant={PriceVariant.DEFAULT}
              priceTagIcon={priceTagIcon}
              cardType={cardType}
              variation={variation}
              ruleExpression={ruleExpression}
              index={index}
              elements={elements}
              tokenProperties={tokenProperties}
            />
          )
        }
      }

      return (
        <div id={contentFragmentIdValue}>
          <div
            data-testid={`react-product-comparison-teaser-card-sm ${index} ${id}`}
            className='react-product-comparison-teaser-container react-product-comparison-teaser-card-sm'
          >
            <div className='product-teaser-section1'>
              {contentBadgeTextValue && (
                <ReactBadge
                  contentBadgeText={contentBadgeTextValue}
                  contentBadgeColor={contentBadgeColorValue}
                  isInContainer={true}
                />
              )}
              <div className='plan-details'>
                {eyebrowValue && <ReactText className={offerElementsClasses.eyebrow} text={eyebrowValue} isParsed />}
                <div className='heading-details'>
                  <div className='heading-icon-details'>
                    {hasHeadingIcon && (
                      <img
                        src={headingIconValue}
                        alt={headingIconAltTextValue}
                        className={`heading-icon ${headingIconValue && 'icon-present'}`}
                      />
                    )}
                    <div className={offerElementsClasses.heading}>
                      {headingValue && (
                        <ReactText className={offerElementsClasses.headingValue} text={headingValue} isParsed />
                      )}
                      {headingUnitValue && (
                        <ReactText className={offerElementsClasses.headingUnit} text={headingUnitValue} isParsed />
                      )}
                    </div>
                  </div>
                  {additionalNoteValue && <ReactText className='additional-note' text={additionalNoteValue} isParsed />}
                </div>
              </div>
            </div>
            <hr className='teaser-section-divider' />
            <div className='product-teaser-section2'>
              {descriptionValue && <ReactText className='description' text={descriptionValue} isParsed />}
              {bulletPoints.value.length > 0 && (
                <ReactList
                  listFrom={ListType.cfConditionalList}
                  elements={elements}
                  ruleExpression={ruleExpression}
                  index={index}
                  tokenProperties={tokenProperties}
                  maxLength={4}
                />
              )}
            </div>
            <hr className='teaser-section-divider' />
            <div className='product-teaser-section3'>{getMainPriceDetails()}</div>
            <hr className='teaser-section-divider' />
            <div className='product-teaser-section4'>{getProductComparisonTeaserButtons()}</div>
          </div>
          <div
            data-testid={`react-product-comparison-teaser-card-lg ${index} ${id}`}
            className='react-product-comparison-teaser-container react-product-comparison-teaser-card-lg'
          >
            <div className='product-teaser-section1-lg'>
              {contentBadgeTextValue ? (
                <ReactBadge
                  contentBadgeText={contentBadgeTextValue}
                  contentBadgeColor={contentBadgeColorValue}
                  isInContainer={true}
                />
              ) : (
                <div className='badge-placeholder'></div>
              )}
              {bulletPoints.value.length > 0 && (
                <ReactList
                  listFrom={ListType.cfConditionalList}
                  elements={elements}
                  ruleExpression={ruleExpression}
                  index={index}
                  tokenProperties={tokenProperties}
                  maxLength={4}
                />
              )}
            </div>
            <hr className='teaser-section-divider' />
            <div className='product-teaser-section2-lg'>
              <div className='product-teaser-section2-part1'>
                <div className='product-teaser-section2-col1'>
                  <div className='heading-icon-details'>
                    {hasHeadingIcon && (
                      <img
                        src={headingIconValue}
                        alt={headingIconAltTextValue}
                        className={`heading-icon ${headingIconValue && 'background-none'}`}
                      />
                    )}
                    <div className={offerElementsClasses.heading}>
                      {headingValue && (
                        <ReactText className={offerElementsClasses.headingValue} text={headingValue} isParsed />
                      )}
                      {headingUnitValue && (
                        <ReactText className={offerElementsClasses.headingUnit} text={headingUnitValue} isParsed />
                      )}
                    </div>
                  </div>
                  {additionalNoteValue && (
                    <ReactText className='additional-note cox-text-paragraph4-medium' text={additionalNoteValue} isParsed />
                  )}
                </div>
                <div className='product-teaser-section2-col2'>
                  {eyebrowValue && <ReactText className={offerElementsClasses.eyebrow} text={eyebrowValue} isParsed />}
                  {descriptionValue && <ReactText className='description' text={descriptionValue} isParsed />}
                </div>
                <div className='product-teaser-section2-col3'>
                  {(pricedropdownValue[0]?.price || priceValue.length > 0) && (
                    <div className='price-dropdown-section'>{getMainPriceDetails()}</div>
                  )}
                </div>
              </div>
              <div className='product-teaser-section2-part2'>
                <div className='product-teaser-buttons-section'>{getProductComparisonTeaserButtons()}</div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    [contentFragments]
  )

  return (
    <ReactTeaserCard
      contentFragments={contentFragments}
      ruleExpression={ruleExpression}
      ruleType={ruleType}
      condition={condition}
      contentType={contentType}
      maxSlides={maxSlides}
      renderCard={renderCard}
    />
  )
}

export default ReactProductComparisonTeaser
